/**
 * Profanity & Offensive Username Filter
 * Normalizes leetspeak, accents, spacing, and symbols to prevent circumvention.
 */

// Core list of offensive stems and terms in Spanish and English
const BANNED_PATTERNS = [
  // Spanish terms & variations
  'puto', 'puta', 'putita', 'puton', 'maricon', 'marica', 'marik', 'mierda', 'pendejo', 
  'pendeja', 'culero', 'cabron', 'cabrona', 'coño', 'verga', 'pito', 'chupala', 'chinga', 
  'chingada', 'chingon', 'zorra', 'zorrita', 'perra', 'gilipollas', 'subnormal', 'tarado', 
  'retrasado', 'mongolo', 'estupido', 'estupida', 'idiota', 'imbecil', 'malparido', 'gonorrea', 
  'hijueputa', 'hijodeputa', 'carechimba', 'pirobo', 'culiao', 'weon', 'conchetumadre', 'ctm',
  'nazi', 'hitler', 'violador', 'violacion', 'pedofilo', 'terrorista', 'suicida', 'asesino',
  
  // English terms & variations
  'fuck', 'fucker', 'fucking', 'shit', 'bitch', 'asshole', 'bastard', 'cunt', 'dick', 
  'cock', 'penis', 'vagina', 'boobs', 'nigger', 'nigga', 'fag', 'faggot', 'whore', 
  'slut', 'retard', 'retarded', 'porn', 'porno', 'sex', 'sexy', 'horny', 'kill', 'die'
];

/**
 * Normalizes input text by replacing leetspeak characters, accents, and repeated letters
 */
export function normalizeText(text: string): string {
  let normalized = text.toLowerCase().trim();

  // Remove accents
  normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Leetspeak substitutions
  const leetMap: Record<string, string> = {
    '0': 'o',
    '1': 'i',
    '!': 'i',
    '|': 'i',
    '3': 'e',
    '4': 'a',
    '@': 'a',
    '5': 's',
    '$': 's',
    '7': 't',
    '+': 't',
    '8': 'b',
    '9': 'g',
    'v': 'u',
    'w': 'u'
  };

  normalized = normalized.split('').map(char => leetMap[char] || char).join('');

  // Remove symbols, spaces, underscores, hyphens
  normalized = normalized.replace(/[^a-z0-9]/g, '');

  // Collapse consecutive repeated letters (e.g., 'puuutttooo' -> 'puto')
  normalized = normalized.replace(/(.)\1+/g, '$1');

  return normalized;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
  flaggedWord?: string;
}

/**
 * Validates a proposed username
 */
/**
 * Apodo con el que alguien entra a una sala.
 *
 * NO es un nombre de usuario: no identifica una cuenta, solo se proyecta en la
 * pantalla. Por eso admite lo que admite un nombre escrito por una persona
 * —espacios, tildes, apóstrofos, cualquier alfabeto— y no el subconjunto ASCII
 * que exige un identificador.
 *
 * La regla anterior era `[a-zA-Z0-9_-]`, que rechazaba "María", "Jose Luis" y
 * prácticamente cualquier nombre en español. Como el rechazo se resolvía
 * renombrando a "Invitado 1234" sin avisar, quien lo sufría no tenía forma de
 * saber qué había hecho mal.
 */
export function validateUsername(username: string): ValidationResult {
  // Los espacios seguidos se colapsan: "Ana    Pérez" y "Ana Pérez" son la
  // misma persona escribiendo con prisa.
  const trimmed = username.trim().replace(/\s+/g, ' ');

  if (!trimmed || trimmed.length < 3) {
    return {
      valid: false,
      error: 'El apodo debe tener al menos 3 caracteres.'
    };
  }

  if (trimmed.length > 18) {
    return {
      valid: false,
      error: 'El apodo no puede pasar de 18 caracteres.'
    };
  }

  // Letras de cualquier alfabeto (\p{L}), marcas diacríticas (\p{M}), dígitos,
  // y los separadores que aparecen en un nombre. Fuera quedan los símbolos y
  // los caracteres de control, que es de lo que se trataba protegerse.
  if (!/^[\p{L}\p{M}\p{N} _'’.-]+$/u.test(trimmed)) {
    return {
      valid: false,
      error: 'El apodo solo puede llevar letras, números, espacios, guiones y apóstrofos.'
    };
  }

  // Un apodo que es solo signos no dice quién eres y desaparece en la pantalla.
  if (!/[\p{L}\p{N}]/u.test(trimmed)) {
    return {
      valid: false,
      error: 'El apodo necesita alguna letra o número.'
    };
  }

  // Profanity detection
  const normalized = normalizeText(trimmed);

  for (const pattern of BANNED_PATTERNS) {
    if (normalized.includes(pattern)) {
      return {
        valid: false,
        error: 'El nombre contiene lenguaje no permitido u ofensivo. Por favor elige uno apropiado.',
        flaggedWord: pattern
      };
    }
  }

  return { valid: true };
}
